import { FC, useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Breadcrumbs,
  Link,
  Typography,
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@material-ui/core";
import ChevronRightIcon from "src/icons/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import { IColumn } from "src/components/dataTable/tableModel";
import InviteUserModal from "./InviteUserModal";
import ConfigUserGroupType from "../userGroups/Types/ConfigUserGroup";
import { getConfigUserGroupsList } from "../userGroups/ConnectionService";
import {
  deleteConfigUserInvitation,
  enableDisableConfigUser,
  getConfigUsersList,
  invalidateConfigUserInvitation,
  resendConfigUserInvitation,
} from "./ConnectionService";
import ConfigUserTable from "./ConfigUsersTable";
import { UserInvitation } from "./Types/UserInvitation";
import { Helmet } from "react-helmet-async";

const columns: IColumn[] = [
  { field: "Username", headerName: "User Name", align: "left" },
  { field: "FullName", headerName: "Full Name", align: "left" },
  { field: "Email", headerName: "Email Address" },
  { field: "ConfigUserGroup", headerName: "User Group", align: "left" },
  {
    field: "IsEnabled",
    headerName: "Is Enabled",
    dataType: "boolean",
    allowInlineChangeForBoolean: true,
    align: "left",
  },
  { field: "Status", headerName: "Status", align: "left" },
];

const styles = {
  yesButton: {
    backgroundColor: "#072f40",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#072f40", // Keeps the background color the same on hover
    },
  },
  noButton: {
    borderColor: "black",
    color: "black",
    "&:hover": {
      borderColor: "black", // Keeps the border color the same on hover
      color: "black", // Keeps the text color the same on hover
    },
  },
};

const ConfigUsersSummary: FC = () => {
  const theme = useTheme();
  const [configUsers, setConfigUsers] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [configUserGroups, setConfigUserGroups] = useState<ConfigUserGroupType[]>([]);
  const [selectedInvitationKey, setSelectedInvitationKey] = useState<string | null>();
  const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);
  const [deleteInvite, setDeleteInvite] = useState<UserInvitation | null>(null);

  const handleModalClose = () => {
    fetchConfigUsersData();
    setShowInviteModal(false);
    setSelectedInvitationKey(null);
  };

  const handleCreateNew = () => {
    setShowInviteModal(true);
  };

  const handleEditClick = async (row: any) => {
    setSelectedInvitationKey(row?.UserInvitationKey);
    setShowInviteModal(true);
  };

  const handleResendEmail = async (data) => {
    const res = await resendConfigUserInvitation(data.UserInvitationKey);
    if (res) {
      fetchConfigUsersData();
    }
  };

  const handleEnabledChange = async (row) => {
    const res = await enableDisableConfigUser(row.ConfigUserKey, !row.IsDisabled);
    if (res) {
      fetchConfigUsersData();
    }
  };

  const handleInvalidateInvite = async (data) => {
    const res = await invalidateConfigUserInvitation(data.UserInvitationKey);
    if (res) {
      fetchConfigUsersData();
    }
  };

  const initiateDelete = async (data) => {
    setDeleteInvite(data);
    setShowDeleteWarning(true);
  };

  const handleDeleteCancel = () => {
    setDeleteInvite(null);
    setShowDeleteWarning(false);
  };

  const handleDeleteConfirm = async (data) => {
    const res = await deleteConfigUserInvitation(
      deleteInvite.UserInvitationKey
    );
    if (res) {
      fetchConfigUsersData();
      setShowDeleteWarning(false);
    }
  };

  const fetchConfigUserGroupData = async () => {
    const res = await getConfigUserGroupsList();
    setConfigUserGroups(res);
  };

  const fetchConfigUsersData = async () => {
    setIsLoading(true);
    const res = await getConfigUsersList();
    const updatedRes = res?.map((x) => {
      return {
        ...x,
        Username: x.Username || "-",
        FullName: x.FullName || "-",
        Email: x.Email || "-",
        ConfigUserGroup: x.ConfigUserGroup || "-",
        IsEnabled: x.IsDisabled === null ? "-" : !x.IsDisabled,
        Status: x.Status || "-",
      };
    });
    setConfigUsers(updatedRes);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchConfigUsersData();
    fetchConfigUserGroupData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Users</title>
      </Helmet>
      <Box>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Administration
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Users
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Box sx={{ my: 2 }}>
                  <Button
                    sx={{
                      background: "linear-gradient(to bottom, #072f40, #072f40)",
                      minWidth: 100,
                    }}
                    variant="contained"
                    onClick={handleCreateNew}
                  >
                    Create New
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <ConfigUserTable
              columns={columns}
              dataList={configUsers}
              isLoading={isLoading}
              onClickEdit={handleEditClick}
              handleResendEmail={handleResendEmail}
              onClickDelete={initiateDelete}
              handleInvalidateInvite={handleInvalidateInvite}
              handleEnabledChange={handleEnabledChange}
            />
          </Card>
        </Container>
      </Box>
      <InviteUserModal
        open={showInviteModal}
        handleClose={handleModalClose}
        userGroupsList={configUserGroups}
        invitationKey={selectedInvitationKey}
      />
      <Dialog open={showDeleteWarning} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure to delete the invite sent to {deleteInvite?.Email}?
            This action is irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={styles.noButton}
          >
            No
          </Button>
          <Button onClick={handleDeleteConfirm} sx={styles.yesButton} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfigUsersSummary;
